import Box from "@mui/material/Box";
import useViewportSize from "common/utilities/hooks/useViewportSize/useViewportSize";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import { useSelector } from "react-redux";
import type {
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceType,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import type { HandleInsertSequences } from "sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser";
import { calcVisualisationsHeight } from "sequenceAnalyser/components/Sequences/SequenceTableVisualisations/utils.ts";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { SelectedColorScheme } from "sequenceAnalyser/utilities/colors";
import {
  RESIDUE_PIXEL_WIDTH,
  sequencesChunkWidth,
  visualisationsStartFrom,
} from "sequenceAnalyser/utilities/screen/screen";
import type { FeatureDefinitions } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { SequenceAnalyserVisualisations } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { hasAnyFeatureDefinitionVisible } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

import ConsensusVisualization from "./ConsensusVisualization";
import SequenceConservationVisualization from "./SequenceConservationVisualization";
import SequenceLogoVisualization from "./SequenceLogoVisualization";
import classes from "./SequenceTableVisualisations.style";
import { CONSENSUS_HEIGHT, CONSERVATION_HEIGHT, LOGO_HEIGHT } from "./settings";

interface ComponentProps {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  colors?: SelectedColorScheme;
  featureDefinitions: FeatureDefinitions;
  onInsertSequences: HandleInsertSequences;
  sessionSequenceType: SequenceType;
}

function SequenceTableVisualisations({
  alignedSequences,
  sequences,
  columnsLetterOccurrences,
  colors,
  featureDefinitions,
  onInsertSequences,
  sessionSequenceType,
}: ComponentProps) {
  useViewportSize(); // needed to trigger a refresh when the user resize the browser

  const sidebarPixelWidth = useSelector((state: ApplicationState) => state.sequenceAnalyser.sidebarPixelWidth);
  const zoomLevel = useSelector((state: ApplicationState) => state.sequenceAnalyser.zoomLevel);

  const visualisations = useSelector((state: ApplicationState) => state.sequenceAnalyser.settings.visualisations);
  if (visualisations.length === 0) return null;

  const hasColors = colors !== undefined;
  const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);
  const visualisationsPosition = visualisationsStartFrom(
    calcVisualisationsHeight(visualisations, 100),
    alignedSequences,
    hasColors,
    hasFeatures,
    zoomLevel,
  );

  // Always use a minimum of the original width for the render to work when zooming out
  const width = sequencesChunkWidth(alignedSequences, sidebarPixelWidth, zoomLevel < 100 ? 100 : zoomLevel);
  const sequencesWidth = RESIDUE_PIXEL_WIDTH * Object.keys(columnsLetterOccurrences).length;

  return (
    <Box
      sx={{
        ...classes.root,
        [visualisationsPosition[0]]: `${visualisationsPosition[1]}px`,
      }}
    >
      {visualisations.includes(SequenceAnalyserVisualisations.consensus) && (
        <ConsensusVisualization
          sidebarPixelWidth={sidebarPixelWidth}
          width={width}
          consensusHeight={CONSENSUS_HEIGHT}
          sequences={sequences}
          onInsertSequences={onInsertSequences}
          sessionSequenceType={sessionSequenceType}
          columnsLetterOccurrences={columnsLetterOccurrences}
        />
      )}

      {visualisations.includes(SequenceAnalyserVisualisations.conservation) && (
        <SequenceConservationVisualization
          sidebarPixelWidth={sidebarPixelWidth}
          width={width}
          sequencesWidth={sequencesWidth}
          conservationHeight={CONSERVATION_HEIGHT}
          alignedSequences={alignedSequences}
          columnsLetterOccurrences={columnsLetterOccurrences}
          zoomLevel={zoomLevel}
        />
      )}

      {visualisations.includes(SequenceAnalyserVisualisations.logo) && (
        <SequenceLogoVisualization
          sidebarPixelWidth={sidebarPixelWidth}
          width={width}
          sequencesWidth={sequencesWidth}
          logoHeight={LOGO_HEIGHT}
          alignedSequences={alignedSequences}
          sessionSequenceType={sessionSequenceType}
          columnsLetterOccurrences={columnsLetterOccurrences}
          zoomLevel={zoomLevel}
        />
      )}
    </Box>
  );
}

export default SequenceTableVisualisations;
